import React, { useRef, useState } from "react";
import { Upload, FileText } from "lucide-react";
import { useUploadFile } from "../hooks/useUploadFile";
import type { UploadResponse } from "../api/endpoints/upload";
import { useDetectPdfType } from "../hooks/useDetectPdfType";

interface UploadFormPanelProps {
  onSuccess: (response: UploadResponse) => void;
}

export function UploadFormPanelNew({ onSuccess }: UploadFormPanelProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [apiMessageError, setApiMessageError] = useState<string | null>(null);
  const [wasUploadAccepted, setWasUploadAccepted] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const smoothProgressRef = useRef<NodeJS.Timeout | null>(null);
  const targetProgressRef = useRef<number>(0);
  const { detect } = useDetectPdfType();

  const resetFileInputElement = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const clearProcessingTimeout = () => {
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
  };

  const clearSmoothProgress = () => {
    if (smoothProgressRef.current) {
      clearTimeout(smoothProgressRef.current);
      smoothProgressRef.current = null;
    }
  };

  const smoothProgressUpdate = (targetProgress: number) => {
    targetProgressRef.current = targetProgress;
    
    const animate = () => {
      setProgress((currentProgress) => {
        const target = targetProgressRef.current;
        if (currentProgress >= target) {
          return target;
        }
        
        // Increment by 1-2% each step for smooth animation
        const increment = Math.max(1, Math.ceil((target - currentProgress) / 10));
        const nextProgress = Math.min(currentProgress + increment, target);
        
        if (nextProgress < target) {
          smoothProgressRef.current = setTimeout(animate, 100);
        } else if (target >= 99 && !isProcessing) {
          // When we reach 99%, start the processing timer
          clearProcessingTimeout();
          processingTimeoutRef.current = setTimeout(() => {
            setIsProcessing(true);
          }, 2000);
        }
        
        return nextProgress;
      });
    };
    
    clearSmoothProgress();
    smoothProgressRef.current = setTimeout(animate, 50);
  };

  const { mutate, isPending, isError, error, reset, isSuccess } = useUploadFile(
    {
      onProgress: (p) => {
        smoothProgressUpdate(p);
      },
      onSuccess: (response) => {
        clearProcessingTimeout();
        clearSmoothProgress();
        setProgress(0);
        setIsProcessing(false);
        targetProgressRef.current = 0;
        const normalizedMessage = (response.message ?? "")
          .trim()
          .replace(/\s+/g, " ")
          .toLowerCase();

        const isPdfUploadedSuccessfully =
          normalizedMessage === "pdf uploaded successfully.";


        if (isPdfUploadedSuccessfully) {
          setApiMessageError(null);
          setWasUploadAccepted(true);
          onSuccess(response);
        } else {
          setWasUploadAccepted(false);
          setApiMessageError(response.message || "Upload failed");
        }
      },
      onError: () => {
        clearProcessingTimeout();
        clearSmoothProgress();
        setProgress(0);
        setIsProcessing(false);
        targetProgressRef.current = 0;
        setWasUploadAccepted(false);
      },
    }
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear any previous mutation state/errors when selecting a file
    clearProcessingTimeout();
    clearSmoothProgress();
    reset();
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);

    } else {
      setFile(null);
      setProgress(0);
      reset();
    }
    setApiMessageError(null);
    setWasUploadAccepted(false);
    setIsProcessing(false);
    targetProgressRef.current = 0;
  };


  const handleUpload = async () => {
    if (!file) return;
    const result = await detect(file);
    const isXFA = result?.isXFA === true;
    setProgress(0);
    setApiMessageError(null);
    setWasUploadAccepted(false);
    setIsProcessing(false);
    clearProcessingTimeout();
    clearSmoothProgress();
    targetProgressRef.current = 0;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("pdfType", isXFA ? "XFA_PDF" : "ACROFORM_PDF");
    mutate(formData);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-4">
          Upload a PDF form and our AI will automatically extract the structure,
          fields, and create an interactive digital version.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-gray-700">
            Supported: PDF files up to 50MB. The AI will identify sections,
            subsections, and field types automatically.
          </p>
        </div>
      </div>

      <div
        onClick={() => {
          if (!file && !isPending) {
            inputRef.current?.click();
          }
        }}
        className={`border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-gray-400 hover:bg-gray-50 transition mb-6 ${
          file || isPending ? "cursor-not-allowed opacity-60" : "cursor-pointer"
        }`}
        aria-disabled={!!file || isPending}
      >
        <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
        <p className="text-sm font-normal text-gray-900 mb-1">
          Click to upload or drag and drop
        </p>
        <p className="text-xs text-gray-500">PDF files only</p>
        <input
          ref={inputRef}
          id="file-input-panel-new"
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          className="hidden"
      
          disabled={!!file || isPending}
        />
      </div>

      {file && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white border border-gray-200 rounded">
              <FileText className="w-4 h-4 text-gray-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-normal text-gray-900 truncate">
                {file.name}
              </p>
              <p className="text-xs text-gray-500">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setProgress(0);
                reset();
                resetFileInputElement();
                setApiMessageError(null);
                setWasUploadAccepted(false);
                setIsProcessing(false);
                clearProcessingTimeout();
                clearSmoothProgress();
                targetProgressRef.current = 0;
              }}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {(isPending || progress > 0) && !isProcessing && (
        <>
          <div className="text-sm text-gray-700 mb-1 text-center">
            {progress}%
          </div>
          <div className="h-2 w-full bg-gray-200 rounded mb-2">
            <div
              className="h-2 bg-blue-600 rounded"
              style={{
                width: `${progress}%`,
                transition: "width 150ms ease-in-out",
              }}
            />
          </div>
        </>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || isPending}
        className="btn-primary w-full disabled:bg-gray-300"
      >
        {isPending
          ? !isProcessing
            ? "Uploading…"
            : "Processing..."
          : "Start PDF Processing"}
      </button>

      {(isError || apiMessageError) && (
        <div className="text-xs text-red-600 mt-2">
          {apiMessageError ?? getErrorMessage(error)}
        </div>
      )}

      {isSuccess && wasUploadAccepted && (
        <div className="text-xs text-green-700 mt-2">Upload complete</div>
      )}
    </div>
  );
}

function getErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error) {
    const maybeMessage = (error as { message?: unknown }).message;
    if (typeof maybeMessage === "string") return maybeMessage;
  }
  return "Upload failed";
}
